import LoadIndicator from 'devextreme-react/load-indicator';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { apiFetch, readJson } from '../api/client';
import { openRegisteredStorageFile } from '../editorShell/editorOpenHelpers';
import {
  fileAccessRestrictedHref,
  isFileAccessRestrictedStatus
} from '../storageAccess/fileAccessRestrictedUrls';

type ResolveResponse = {
  mountId: string;
  path: string;
  viewerRoute: string;
};

export interface OpenByStoragePageProps {
  filesLinkTo?: string;
  filesLinkLabel?: string;
}

export function OpenByStoragePage({
  filesLinkTo = '/files',
  filesLinkLabel = 'File Browser'
}: OpenByStoragePageProps) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const fp = (params.get('fp') ?? '').trim();
  const path = (params.get('path') ?? '').trim();
  const spath = (params.get('spath') ?? '').trim();
  const discussionAnchor = (params.get('discussionAnchor') ?? '').trim();

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      if (!fp || !path) {
        setError('This link is missing storage or file path information.');
        return;
      }

      const query = new URLSearchParams({ fp, path });
      if (spath) query.set('spath', spath);
      const res = await apiFetch(`/api/file-review/open-by-fingerprint?${query.toString()}`);
      if (cancelled) return;

      if (!res.ok) {
        if (isFileAccessRestrictedStatus(res.status)) {
          navigate(fileAccessRestrictedHref(), { replace: true });
          return;
        }
        const body = await readJson<{ message?: string }>(res).catch(() => null);
        setError(
          body?.message ??
            'Could not open this file on this site. The storage mount may not be configured with a matching FileReviewStorageKey.'
        );
        return;
      }

      const data = (await readJson<ResolveResponse>(res)) as ResolveResponse;
      const resolvedPath = (data.path ?? path).trim() || path;
      const fileName = resolvedPath.split('/').pop() || resolvedPath;
      const opened = openRegisteredStorageFile({
        fileName,
        mountId: data.mountId,
        path: resolvedPath,
        navigate: (to) => {
          const target = new URL(to, window.location.origin);
          if (discussionAnchor) target.searchParams.set('discussionAnchor', discussionAnchor);
          navigate(`${target.pathname}${target.search}`, { replace: true });
        }
      });
      if (opened) return;

      const viewerParams = new URLSearchParams({
        mountId: data.mountId,
        path: resolvedPath
      });
      if (discussionAnchor) {
        viewerParams.set('discussionAnchor', discussionAnchor);
      }

      navigate(`${data.viewerRoute}?${viewerParams.toString()}`, { replace: true });
    }

    void resolve().catch(() => {
      if (!cancelled) {
        setError('Could not resolve this file link. Try opening the file browser and locating the file manually.');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [discussionAnchor, fp, navigate, path, spath]);

  if (error) {
    return (
      <div style={{ padding: '2rem', maxWidth: 560 }}>
        <h2>Could not open file</h2>
        <p>{error}</p>
        <p>
          <a href={filesLinkTo}>{filesLinkLabel}</a>
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <LoadIndicator height={28} width={28} visible />
      <span>Opening file…</span>
    </div>
  );
}
